<template>
  <main class="prizes bb">
    <section>
      <PageBackButton />

      <h1 class="pg_title">{{ $_translations.value.page_leaderboard_title }}</h1>

      <div :ref="leaderboardContainerRef">
        <section v-if="!initialFetching" class="tabs">
          <div class="tab-contents">
            <div class="tab-content active">
              <PrizeSection v-if="hasPrizes">
                <template v-slot>
                  <PrizeSelectionCarousel
                    :model-value="activeCategory"
                    :prize-list="prizeList"
                    @update:modelValue="handlePrizeChange"
                  />
                </template>
              </PrizeSection>

              <CurrentUserSection
                :user="activeCategoryUserCurrent"
                :loading="fetchingLeaderboardData"
              />

              <UsersSection
                :has-expand-button="totalActiveCategoryUserList > INITIAL_VISIBLE_USERS"
                :expanded="activeCategoryUserListExpanded"
                :loading="fetchingLeaderboardData"
                @load:all="handleLoadMoreUsers"
              >
                <template v-slot>
                  <template v-if="totalActiveCategoryUserList > 0">
                    <UserItem
                      v-for="(user, index) in filteredActiveCategoryUserList"
                      :key="`${activeCategory}_${user.msisdn}`"
                      :user="user"
                      :animated="index > INITIAL_VISIBLE_USERS"
                      @view:profile="handleViewProfile"
                    />
                  </template>

                  <template v-else>
                    <p class="empty_leader">
                      {{ $_translations.value.page_leaderboard_section_users_list_empty }}</p>
                  </template>
                </template>
              </UsersSection>
            </div>
          </div>
        </section>
      </div>
    </section>
  </main>

  <UserProfileModal
    v-if="isUserProfileModalOpen"
    :user-msisdn="selectedUserForViewProfile"
  />
</template>

<script setup>
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import PageBackButton from '@/components/button/PageBackButton.vue';
import PrizeSelectionCarousel from '@/components/carousel/PrizeSelectionCarousel.vue';
import UserProfileModal from '@/components/modal/UserProfileModal.vue';
import PrizeSection from '@/views/leaderboard/components/section/PrizeSection.vue';
import CurrentUserSection from '@/views/leaderboard/components/section/CurrentUserSection.vue';
import UsersSection from '@/views/leaderboard/components/section/UsersSection.vue';
import UserItem from '@/views/leaderboard/components/item/UserItem.vue';
import useLocaleStore from '@/store/modules/locale.js';
import useAuthStore from '@/store/modules/auth.js';
import useUILockHook from '@/hooks/useUILock.js';
import useModalHook from '@/hooks/useModal.js';
import { retrievePrizes } from '@/services/prizes.js';
import { apiRetrieveLeaderboardData } from '@/services/leaderboard.js';
import { prizeTypeEnum } from '@/constants/winners.js';
import { leaderboardCategoryEnum } from '@/constants/leaderboard.js';

const INITIAL_VISIBLE_USERS = 5;

const leaderboardContainerRef = 'leaderboard-container-ref';

const leaderboardPerCategoryInitialValue = Object.values(leaderboardCategoryEnum).reduce((acc, category) => {
  acc[category] = {
    fetched: false,
    user: { current: null, list: [], listExpanded: false },
  };

  return acc;
}, {});

const localeStore = useLocaleStore();
const authStore = useAuthStore();

const leaderboardContainerTemplateRef = useTemplateRef(leaderboardContainerRef);

const [fetchingLeaderboardData, setFetchingLeaderboardDataState] = useUILockHook({ targetElementTemplateRef: leaderboardContainerTemplateRef });
const [isUserProfileModalOpen, openUserProfileModal] = useModalHook();

const initialFetching = ref(false);
const prizeList = ref([]);
const leaderboardPerCategory = ref(leaderboardPerCategoryInitialValue);
const selectedUserForViewProfile = ref('');
const activeCategory = ref('');

const activeCategoryUserCurrent = computed(() => leaderboardPerCategory.value[activeCategory.value]?.user?.current || []);
const activeCategoryUserList = computed(() => leaderboardPerCategory.value[activeCategory.value]?.user?.list || []);
const totalActiveCategoryUserList = computed(() => activeCategoryUserList.value.length);
const activeCategoryUserListExpanded = computed(() => leaderboardPerCategory.value[activeCategory.value]?.user?.listExpanded || false);
const filteredActiveCategoryUserList = computed(() => ((totalActiveCategoryUserList.value > INITIAL_VISIBLE_USERS) && !activeCategoryUserListExpanded.value)
  ? activeCategoryUserList.value.slice(0, INITIAL_VISIBLE_USERS)
  : activeCategoryUserList.value,
);
const hasPrizes = computed(() => prizeList.value.length > 0);

const fetchPrizeList = async () => {
  try {
    const { data } = await retrievePrizes(localeStore.currentLanguage);

    if (Array.isArray(data) && (data.length > 0)) {
      const sortMap = {
        [prizeTypeEnum.WEEKLY]: 1,
        [prizeTypeEnum.MONTHLY]: 2,
        [prizeTypeEnum.GRAND]: 3,
      };

      prizeList.value = data.toSorted((a, b) => sortMap[a.type] - sortMap[b.type]);

      activeCategory.value = prizeList.value[0]?.type;
    }
  } catch {
    // Prize list fetching errors are ignored
  }
};

const fetchLeaderboardData = async ({ category }) => {
  try {
    if (!Object.hasOwn(leaderboardPerCategory.value, category) || leaderboardPerCategory.value[category].fetched) {
      return;
    }

    setFetchingLeaderboardDataState(true);

    const payload = {
      language: localeStore.currentLanguage,
      prizeType: category,
    };

    const { parsedData } = await apiRetrieveLeaderboardData({ payload });

    let currentUser = null;

    if (Array.isArray(parsedData?.userRankPointsDTOList)) {
      currentUser = parsedData.userRankPointsDTOList.find(({ msisdn }) => msisdn === authStore.user.msisdn);

      leaderboardPerCategory.value[category].user.list = parsedData.userRankPointsDTOList.map(userInfo => ({
        ...userInfo,
        __profileViewRestricted: (userInfo.msisdn === authStore.user.msisdn) || userInfo.hasPrivateProfile,
      }));
    }

    leaderboardPerCategory.value[category].user.current = {
      username: currentUser?.username || authStore.userUsername,
      avatar: currentUser?.avatar || authStore.userAvatar,
      points: currentUser?.points,
      rank: currentUser?.rank,
    };

    leaderboardPerCategory.value[category].fetched = true;

    setFetchingLeaderboardDataState(false);
  } catch {
    setFetchingLeaderboardDataState(false);
  }
};

const handlePrizeChange = (value) => {
  activeCategory.value = value;

  fetchLeaderboardData({ category: value });
};

const handleLoadMoreUsers = () => {
  leaderboardPerCategory.value[activeCategory.value].user.listExpanded = true;
};

const handleViewProfile = ({ msisdn }) => {
  selectedUserForViewProfile.value = msisdn;

  openUserProfileModal({
    afterHideCallback: () => {
      selectedUserForViewProfile.value = '';
    },
  });
};

onMounted(async () => {
  initialFetching.value = true;

  await fetchPrizeList();

  await fetchLeaderboardData({ category: activeCategory.value });

  initialFetching.value = false;
});
</script>
